import moment from "moment";
import { Col, Row, Typography } from "antd";
import { TooltipIndexExplains } from "radar/components";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { Charts } from "shared/components";
import ArtistComparator from "./ArtistComparator";
import { ReactComponent as ExportIcon } from "shared/assets/images/cloud-download.svg";
import "./Comparison.scss";
import ExportComparison from "./export-comparison/ExportComparison";
import CardHeader from "shared/components/card/CardHeader";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";

const Comparison: React.FC<RouteComponentProps> = () => {
  const [artists, setArtists] = useState<any>([]);
  const [exporting, setExporting] = useState<boolean>(false);
  const [history, setHistory] = useState<any>({
    series: [],
    categories: [],
  });
  const [artistIndexes, setArtistIndexes] = useState<any>({
    series: [],
    categories: [],
  });
  const { t } = useTranslation("radar");
  const onCompare = useCallback((comparedArtists: any) => {
    setArtists(comparedArtists);
  }, []);

  const onClear = useCallback(() => {
    setArtists([]);
  }, []);

  const prepareExport = () => {
    setExporting(true);
  };

  useEffect(() => {
    const getHistory = () => {
      const reduced = artists.reduce(
        (prev: any, curr: any) => {
          if (!prev.categories)
            prev.categories = curr.history?.map(({ date }: any) =>
              moment(date).format("MMM DD, YYYY")
            );
          prev.series.push({
            name: curr.artistMetadata.artistName,
            data: curr.history?.map(
              ({ performanceIndex }: any) => performanceIndex
            ),
          });
          return prev;
        },
        {
          series: [] as any,
        }
      );

      return reduced;
    };

    const proccessArtists = () => {
      const categories = [
        t("pages.Comparison.performance").toUpperCase(),
        t("pages.Comparison.development").toUpperCase(),
        t("pages.Comparison.music").toUpperCase(),
        t("pages.Comparison.image").toUpperCase(),
        t("pages.Comparison.volatility").toUpperCase(),
      ];
      const series: any = artists.map((artist: any) => ({
        name: `${artist.artistMetadata.artistName} (${t(
          "pages.Comparison.stage",
          {
            stage: artist.strmIndexes.careerStage,
          }
        )})`,
        data: [
          (artist.strmIndexes.performanceIndex || 0).toPrecision(3),
          (artist.strmIndexes.developmentIndex || 0).toPrecision(3),
          (artist.strmIndexes.performanceSpotify || 0).toPrecision(3),
          (artist.strmIndexes.performanceInstagram || 0).toPrecision(3),
          (artist.strmIndexes.volatilityIndex || 0).toPrecision(3),
        ],
      }));
      return {
        categories,
        series,
      };
    };

    setHistory(getHistory());
    setArtistIndexes(proccessArtists());
  }, [artists, t]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Comparator.S4L_ARTIST_COMPARATOR,
    });
  }, []);

  if (exporting)
    return (
      <ExportComparison
        close={() => setExporting(false)}
        {...artistIndexes}
        min={artists[0]?.minPerformance || 0}
        max={artists[0]?.maxPerformance || 0}
        history={history}
      />
    );

  return (
    <div className="comparison">
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Row>
            <Col xs={12}>
              <Typography.Title>
                {t("pages.Comparison.selectArtist")}
              </Typography.Title>
            </Col>
            <Col xs={12}>
              <div style={{ display: "flex", justifyContent: "end" }}>
                <button className="botao-azul" onClick={prepareExport}>
                  <ExportIcon className="download-icon" />
                  {t("pages.ArtistProfile.export")}
                </button>
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={24}>
          <ArtistComparator onCompare={onCompare} onClear={onClear} />
        </Col>
        <Col xs={24}>
          <CardHeader
            header={
              <div style={{ padding: 24 }}>
                {t("pages.Comparison.spotifyPerformance")}
              </div>
            }
          >
            <Charts.LineChart
              color={["#E7E0C9", "#5C8959", "#6C7AA1", "#12C5D0", "#FFADA7"]}
              tickAmount={10}
              min={artists[0]?.minPerformance}
              max={artists[0]?.maxPerformance}
              height={500}
              {...history}
              xAxisFormatter={(val) => (val || 0).toPrecision(3)}
            />
          </CardHeader>
        </Col>

        <Col lg={24} xs={24}>
          <CardHeader
            header={
              <div style={{ padding: 24 }}>
                <Typography.Text
                  style={{ color: "white", textTransform: "uppercase" }}
                >
                  {t("pages.ArtistProfile.strmIndex")} <TooltipIndexExplains />
                </Typography.Text>
              </div>
            }
          >
            <Charts.BarChart
              cor={["#E7E0C9", "#5C8959", "#6C7AA1", "#12C5D0", "#FFADA7"]}
              {...artistIndexes}
              horizontal={false}
              min={0}
              max={100}
              legend={{
                show: true,
                fontSize: "18px",
                position: "top",
                fontFamily: "Cabin",
                itemMargin: { horizontal: 32 },
                markers: { width: 16, height: 16, radius: 50, offsetX: -8 },
                labels: {
                  colors: [
                    "#E7E0C9",
                    "#5C8959",
                    "#6C7AA1",
                    "#12C5D0",
                    "#FFADA7",
                  ],
                  useSeriesColors: true,
                },
              }}
              strokeDashArray={7}
              height={500}
              showGridY
            />
          </CardHeader>
        </Col>
      </Row>
    </div>
  );
};

export default Comparison;
